<template>
	<div
		class="font-['Inter',sans-serif] text-[#0f1f1c] bg-white overflow-x-hidden">
		<!-- ── NAV ── -->
		<nav
			class="flex items-center justify-between px-12 py-4 sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#e4f4f0] transition-shadow duration-300"
			:class="scrolled ? 'shadow-[0_2px_20px_rgba(15,31,28,0.08)]' : ''">
			<span
				class="font-syne text-xl font-extrabold text-[#0d6b5e] flex items-center gap-1.5">
				<span
					class="w-2.5 h-2.5 rounded-full bg-[#1a9b8a] inline-block animate-blink" />
				Trekko
			</span>
			<a
				href="/register"
				class="font-syne text-sm font-bold text-white bg-[#1a9b8a] rounded-lg px-5 py-2.5 hover:bg-[#0d6b5e] transition-all duration-200 hover:-translate-y-0.5">
				Create Event
			</a>
		</nav>

		<!-- ── HERO ── -->
		<section
			class="min-h-[92vh] grid grid-cols-1 md:grid-cols-2 items-center gap-12 px-12 py-16 bg-[#e4f4f0] relative overflow-hidden">
			<div
				class="absolute w-64 h-56 bg-[#f0e040]/50 rounded-[44%_56%_62%_38%/50%_60%_40%_50%] bottom-24 left-[4%] pointer-events-none" />
			<div
				class="absolute w-96 h-80 bg-[#1a9b8a]/10 rounded-[62%_38%_46%_54%/60%_44%_56%_40%] -top-20 right-[6%] pointer-events-none" />

			<!-- Left -->
			<div class="relative z-10">
				<div
					class="inline-flex items-center gap-2 text-xs font-semibold text-[#0d6b5e] bg-white/85 border border-[#d0ede8] rounded-full px-4 py-1.5 mb-6">
					<span
						class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse-green" />
					Effortless event management
				</div>
				<h1
					class="font-syne text-5xl lg:text-[4.3rem] font-extrabold leading-[1.04] tracking-tight text-[#0f1f1c] mb-5">
					Effortless Events,<br />
					<span class="text-[#1a9b8a]">Lasting </span><br />
					Impressions
				</h1>
				<p class="text-base text-[#6b8f89] leading-relaxed max-w-md mb-8">
					From your first listing to sold-out status — Trekko connects you with
					attendees, manages logistics, and is
					<strong class="text-[#0d6b5e] font-semibold">completely free.</strong>
				</p>
				<div class="flex gap-4 flex-wrap">
					<a
						href="/register"
						class="font-syne text-sm font-bold text-white bg-[#0f1f1c] rounded-lg px-7 py-3.5 hover:bg-[#0d6b5e] hover:-translate-y-0.5 transition-all duration-200">
						Start Your Journey Today
					</a>
					<a
						href="https://trekko.xyz/discover"
						class="font-syne text-sm font-bold text-[#0d6b5e] border-2 border-[#1a9b8a] rounded-lg px-6 py-3.5 hover:bg-[#1a9b8a] hover:text-white transition-all duration-200">
						Join the Community →
					</a>
				</div>
			</div>

			<!-- Right — floating UI cards -->
			<div class="relative z-10 hidden md:flex flex-col gap-3">
				<div
					class="bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(15,31,28,0.10)] border border-[#1a9b8a]/10">
					<div class="flex items-center gap-3 mb-3">
						<div
							class="w-10 h-10 rounded-xl bg-[#1a9b8a] flex items-center justify-center font-syne font-bold text-sm text-white flex-shrink-0">
							SO
						</div>
						<div>
							<div class="font-semibold text-sm text-[#0f1f1c]">
								Sound without Borders
							</div>
							<div class="text-xs text-[#6b8f89]">IKAZE</div>
						</div>
						<span
							class="ml-auto text-[10px] font-bold bg-[#d0ede8] text-[#0d6b5e] rounded-full px-2.5 py-0.5"
							>Live</span
						>
					</div>
					<div class="h-1.5 rounded-full bg-[#e4f4f0] overflow-hidden mb-2">
						<div class="h-full w-[72%] bg-[#1a9b8a] rounded-full" />
					</div>
					<div class="flex justify-between text-xs text-[#6b8f89]">
						<span> tickets sold</span><span>72%</span>
					</div>
				</div>

				<div class="flex gap-3">
					<div class="bg-[#1a9b8a] rounded-2xl px-4 py-3 flex-shrink-0">
						<div
							class="font-syne text-[1.8rem] font-extrabold text-white leading-none">
							4.8
						</div>
						<div class="text-[11px] text-white/80 mt-0.5">★ Average Rating</div>
					</div>
					<div
						class="flex-1 bg-white rounded-2xl shadow-[0_4px_16px_rgba(15,31,28,0.08)] border border-[#1a9b8a]/10 flex items-center justify-center gap-2 text-sm font-semibold text-[#0f1f1c]">
						<span class="text-yellow-400">★</span> 5+ Events Hosted
					</div>
				</div>

				<div
					class="bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(15,31,28,0.10)] border border-[#1a9b8a]/10 max-w-[260px]">
					<div
						class="text-[10px] font-bold uppercase tracking-widest text-[#6b8f89] mb-1">
						Users Today
					</div>
					<div
						class="font-syne text-[1.6rem] font-extrabold text-[#0f1f1c] leading-none mb-2.5">
						150+
						<span class="text-sm text-[#1a9b8a] font-semibold">↑ 24%</span>
					</div>
					<div class="flex items-end gap-1 h-10">
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 55%" />
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 70%" />
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 45%" />
						<div class="flex-1 bg-[#1a9b8a] rounded-sm" style="height: 90%" />
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 65%" />
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 80%" />
						<div class="flex-1 bg-[#d0ede8] rounded-sm" style="height: 60%" />
					</div>
				</div>
			</div>
		</section>

		<!-- ── SCROLL STRIP ── -->
		<div class="bg-[#0f1f1c] py-4 overflow-hidden">
			<div class="flex gap-10 whitespace-nowrap animate-slide">
				<template v-for="loop in 2" :key="loop">
					<span
						v-for="item in stripItems"
						:key="`${loop}-${item}`"
						class="font-syne text-[13px] font-bold uppercase tracking-widest text-white/90 inline-flex items-center gap-4">
						{{ item }} <span class="text-[#1a9b8a]">✦</span>
					</span>
				</template>
			</div>
		</div>

		<!-- ── STATS ── -->
		<div class="grid grid-cols-2 md:grid-cols-4 border-b border-[#d0ede8]">
			<div
				v-for="s in stats"
				:key="s.num"
				class="py-10 px-8 text-center border-r border-[#d0ede8] last:border-r-0 hover:bg-[#e4f4f0] transition-colors duration-200 cursor-default">
				<div
					class="font-syne text-4xl font-extrabold text-[#0d6b5e] leading-none mb-1.5">
					{{ s.num }}
				</div>
				<div class="text-sm text-[#6b8f89] leading-snug">{{ s.lbl }}</div>
			</div>
		</div>

		<!-- ── PROBLEM ── -->
		<section
			class="grid grid-cols-1 md:grid-cols-2 gap-16 items-center px-12 py-24 bg-[#e4f4f0]">
			<div class="flex flex-col gap-4">
				<div
					class="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1a9b8a]">
					<span class="w-5 h-px bg-[#1a9b8a]" /> The Problem We Solve
				</div>
				<h2
					class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
					Organizers Were<br />Doing It the Hard Way
				</h2>
				<p class="text-[15px] text-[#6b8f89] leading-relaxed">
					Before Trekko, event organizers in Rome were managing everything
					manually — WhatsApp groups, Instagram DMs, spreadsheets, and chaos at
					the door.
				</p>
				<p class="text-[15px] text-[#6b8f89] leading-relaxed">
					That's not a business — that's survival mode. You deserve tools that
					work as hard as you do.
				</p>
			</div>
			<div class="flex flex-col gap-3">
				<div
					v-for="p in problems"
					:key="p.title"
					class="bg-white rounded-2xl p-4 flex items-start gap-4 border border-[#1a9b8a]/10 hover:border-[#1a9b8a] hover:translate-x-1 transition-all duration-200 cursor-default">
					<span class="text-xl flex-shrink-0 mt-3.5">
						<svg
							viewBox="0 0 48 48"
							fill="none"
							stroke="#1a9b8a"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
							class="w-5 h-5">
							<path :d="p.icon" />
						</svg>
					</span>
					<div>
						<div class="font-semibold text-sm text-[#0f1f1c] mb-1">
							{{ p.title }}
						</div>
						<div class="text-xs text-[#6b8f89] leading-relaxed">
							{{ p.body }}
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- ── BUILT FOR ── -->
		<div class="px-12 py-6">
			<div
				class="bg-[#0f1f1c] rounded-3xl px-16 py-20 relative overflow-hidden">
				<div
					class="absolute w-72 h-64 bg-[#1a9b8a]/18 rounded-[60%_40%_50%_50%/50%_40%_60%_50%] -top-16 -right-14 pointer-events-none" />
				<div class="text-center max-w-2xl mx-auto mb-14 relative z-10">
					<h2
						class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3">
						Built for Every Kind of Event
					</h2>
					<p class="text-[15px] text-white/55 leading-relaxed">
						From food fests to startup launches — Trekko welcomes every
						organizer. If you bring something great to the table, there's a
						place for you.
					</p>
				</div>
				<div class="grid grid-cols-1 md:grid-cols-3 gap-5 relative z-10">
					<div
						v-for="s in steps"
						:key="s.num"
						class="bg-white/6 border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors duration-200">
						<div
							class="w-7 h-7 rounded-full bg-[#1a9b8a] flex items-center justify-center font-syne font-extrabold text-xs text-white mx-auto mb-4">
							{{ s.num }}
						</div>
						<!-- Mock UI per step -->
						<div class="flex justify-center mb-5">
							<div v-if="s.num === '1'" class="flex flex-col gap-1.5 w-3/4">
								<div class="h-2 bg-white/18 rounded" style="width: 70%" />
								<div class="h-2 bg-white/18 rounded" style="width: 50%" />
							</div>
							<div v-else-if="s.num === '2'" class="flex items-center gap-2">
								<span
									class="text-[10px] font-bold bg-[#1a9b8a] text-white rounded-full px-2.5 py-0.5"
									>Verified</span
								>
								<div class="h-2 w-12 bg-white/18 rounded" />
							</div>
							<div v-else class="flex items-end gap-1.5 h-10">
								<div class="w-3 bg-white/15 rounded-sm" style="height: 60%" />
								<div class="w-3 bg-white/15 rounded-sm" style="height: 75%" />
								<div class="w-3 bg-[#1a9b8a] rounded-sm" style="height: 100%" />
								<div class="w-3 bg-white/15 rounded-sm" style="height: 80%" />
							</div>
						</div>
						<div class="font-syne font-bold text-sm text-white mb-1.5">
							{{ s.title }}
						</div>
						<div class="text-xs text-white/50 leading-relaxed">
							{{ s.body }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ── PLATFORM / DASHBOARD ── -->
		<section class="px-12 py-24">
			<div class="text-center mb-12">
				<!-- <div
					class="text-xs font-bold uppercase tracking-widest text-[#1a9b8a] mb-4">
					The Platform
				</div> -->
				<h2
					class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-3">
					Everything You Need.<br />Already Built. Already Working.
				</h2>
				<p class="text-[15px] text-[#6b8f89] max-w-lg mx-auto leading-relaxed">
					Trekko isn't a concept — it's live. Here's what organizers have access
					to from day one.
				</p>
			</div>
			<div class="bg-[#0f1f1c] rounded-3xl p-14 relative overflow-hidden">
				<div
					class="absolute w-72 h-64 bg-[#1a9b8a]/15 rounded-[60%_40%_50%_50%/50%_40%_60%_50%] -top-16 -right-14 pointer-events-none" />
				<div class="max-w-lg mb-10 relative z-10">
					<div
						class="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1a9b8a] mb-3">
						<span class="w-4 h-px bg-[#1a9b8a]" /> Organizer Dashboard
					</div>
					<h3
						class="font-syne text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-3">
						Your Command Center<br />for Every Event
					</h3>
					<p class="text-[15px] text-white/60 leading-relaxed">
						Track ticket sales, revenue, attendees, and event status all in one
						place. No spreadsheets. No guessing. Just clean, real-time data.
					</p>
				</div>
				<div
					class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
					<div
						v-for="f in dashFeatures"
						:key="f.title"
						class="bg-white/6 border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors duration-200">
						<div
							class="w-10 h-10 rounded-xl bg-[#1a9b8a] flex items-center justify-center mb-4">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="white"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="w-5 h-5">
								<path :d="f.icon" />
							</svg>
						</div>
						<div class="font-syne font-bold text-sm text-white mb-1.5">
							{{ f.title }}
						</div>
						<div class="text-xs text-white/50 leading-relaxed">
							{{ f.body }}
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- ── SUBDOMAIN ── -->
		<div class="px-12 pb-8">
			<div
				class="bg-[#1a9b8a] rounded-3xl px-16 py-20 grid grid-cols-1 md:grid-cols-2 gap-16 items-center relative overflow-hidden">
				<div
					class="absolute w-56 h-56 bg-[#1a9b8a]/15 rounded-full -bottom-16 -right-12 pointer-events-none" />

				<div class="relative z-10 flex flex-col gap-4">
					<div
						class="text-xs font-bold uppercase tracking-widest text-[#a8d5cc]">
						Your Brand, Your Page
					</div>
					<h2
						class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
						Every Organizer Gets Their Own
						<em class="not-italic text-[#a8d5cc]">Home on the Web</em>
					</h2>
					<p class="text-[15px] text-white/60 leading-relaxed">
						Stop sending people to five different links. Trekko gives every
						organizer a dedicated subdomain —
						<strong class="text-white/85 font-semibold"
							>yourname.trekko.xyz</strong
						>
						— your personal event hub.
					</p>
					<ul class="flex flex-col gap-2.5 mt-1">
						<li
							v-for="c in subdomainChecks"
							:key="c"
							class="flex items-start gap-3 text-sm text-white/80">
							<span
								class="w-5 h-5 rounded-full bg-[#1a9b8a] flex items-center justify-center flex-shrink-0 mt-0.5">
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="white"
									stroke-width="3"
									class="w-3 h-3">
									<polyline points="20 6 9 17 4 12" />
								</svg>
							</span>
							{{ c }}
						</li>
					</ul>
				</div>

				<!-- Browser mockup -->
				<div
					class="bg-[#0f1f1c] border border-white/10 rounded-2xl overflow-hidden relative z-10">
					<div
						class="bg-white/7 px-4 py-3 flex items-center gap-1.5 border-b border-white/8">
						<span class="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
						<span class="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
						<span class="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
						<div
							class="flex-1 mx-2 bg-white/8 rounded-md px-3 py-1 text-[11px] text-white/50">
							soundon.trekko.xyz
						</div>
					</div>
					<div class="p-5">
						<div class="flex items-center gap-3 mb-5">
							<div
								class="w-12 h-12 rounded-xl bg-[#1a9b8a] flex items-center justify-center font-syne font-extrabold text-lg text-white flex-shrink-0">
								SO
							</div>
							<div>
								<div class="font-syne font-bold text-sm text-white">
									Sound On Events
								</div>
								<div class="text-xs text-white/50 mt-0.5">📍 Rome, Italy</div>
								<span
									class="inline-block text-[10px] font-semibold bg-[#1a9b8a]/25 text-[#a8d5cc] rounded-full px-2 py-0.5 mt-1"
									>✓ Verified Organizer</span
								>
							</div>
						</div>
						<div
							class="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-2">
							Upcoming Events
						</div>
						<div
							v-for="e in mockEvents"
							:key="e.name"
							class="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2.5 mb-2">
							<div>
								<div class="text-sm font-semibold text-white">{{ e.name }}</div>
								<div class="text-[11px] text-white/40 mt-0.5">{{ e.date }}</div>
							</div>
							<span
								class="text-[10px] font-semibold rounded-full px-2.5 py-0.5 flex-shrink-0"
								:class="{
									'bg-[#1a9b8a]/30 text-[#a8d5cc]': e.status === 'ended',
									'bg-white/12 text-white/65': e.status === 'soon',
									'bg-white/8 text-white/50': e.status === 'open',
								}"
								>{{ e.label }}</span
							>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ── KEY FEATURES ── -->
		<section class="px-12 py-24 bg-[#e4f4f0]">
			<div class="text-center mb-12">
				<h2
					class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight mb-2">
					Key Features
				</h2>
				<p class="text-[15px] text-[#6b8f89]">
					Everything you need to create, manage, and grow your events.
				</p>
			</div>
			<div
				class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
				<div
					v-for="f in features"
					:key="f.title"
					class="bg-white rounded-2xl p-6 border border-transparent hover:border-[#a8d5cc] hover:-translate-y-1 transition-all duration-200 cursor-default group">
					<div
						class="w-11 h-11 rounded-xl bg-[#d0ede8] group-hover:bg-[#1a9b8a] flex items-center justify-center mb-4 transition-colors duration-200 text-[#1a9b8a] group-hover:text-white">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="1.8"
							stroke-linecap="round"
							stroke-linejoin="round"
							class="w-5 h-5">
							<path :d="f.icon" />
						</svg>
					</div>
					<div class="font-syne font-bold text-sm text-[#0f1f1c] mb-1.5">
						{{ f.title }}
					</div>
					<div class="text-xs text-[#6b8f89] leading-relaxed">{{ f.body }}</div>
				</div>
			</div>
		</section>

		<!-- ── FAQ ── -->
		<section class="px-12 py-24">
			<h2
				class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-10">
				Frequently Asked<br />Questions
			</h2>
			<div class="border-t border-[#e4f4f0]">
				<div
					v-for="(item, i) in faqs"
					:key="i"
					class="border-b border-[#e4f4f0] cursor-pointer"
					@click="toggleFaq(i)">
					<div class="flex items-center justify-between py-5">
						<span
							class="font-semibold text-[15px] transition-colors duration-200"
							:class="openFaq === i ? 'text-[#1a9b8a]' : 'text-[#0f1f1c]'"
							>{{ item.q }}</span
						>
						<div
							class="w-8 h-8 rounded-full flex items-center justify-center text-lg font-light flex-shrink-0 transition-all duration-300"
							:class="
								openFaq === i
									? 'bg-[#1a9b8a] text-white rotate-45'
									: 'bg-[#d0ede8] text-[#1a9b8a]'
							">
							+
						</div>
					</div>
					<div
						class="text-sm text-[#6b8f89] leading-relaxed overflow-hidden transition-all duration-300 ease-in-out"
						:class="openFaq === i ? 'max-h-48 pb-5' : 'max-h-0'">
						{{ item.a }}
					</div>
				</div>
			</div>
		</section>

		<!-- ── CTA ── -->
		<div class="px-12 pb-12">
			<div
				class="bg-gradient-to-br from-[#0d6b5e] to-[#1a9b8a] rounded-3xl px-16 py-16 flex items-center">
				<div class="max-w-lg">
					<h2
						class="font-syne text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight mb-4">
						Your Best Event<br />Starts Here!
					</h2>
					<p class="text-[15px] text-white/80 leading-relaxed mb-7">
						Join the organizers already using Trekko. Free to start, powerful
						when you scale — and your attendees will thank you for it.
					</p>
					<div class="flex gap-4 flex-wrap">
						<a
							href="/register"
							class="font-syne text-sm font-bold bg-[#f0e040] text-[#0f1f1c] rounded-lg px-7 py-3.5 hover:opacity-90 hover:-translate-y-0.5 transition-all duration-150">
							Start Your Journey →
						</a>
						<a
							href="https://wa.me/+393516148642"
							target="_blank"
							class="font-syne text-sm font-bold bg-white/12 border border-white/30 text-white rounded-lg px-6 py-3.5 hover:bg-white/20 transition-colors duration-200">
							Talk to Us First
						</a>
					</div>
				</div>
			</div>
		</div>

		<!-- ── FOOTER ── -->
		<footer
			class="bg-[#0f1f1c] border-t border-white/6 px-12 py-7 flex items-center justify-between">
			<span class="font-syne font-extrabold text-xl text-white">Trekko</span>
			<span class="text-xs text-white/35">© 2025 All rights reserved.</span>
		</footer>
	</div>
</template>

<script>
export default {
	name: "LandingPage",

	data() {
		return {
			scrolled: false,
			openFaq: 0,

			stripItems: [
				"Seamless Ticketing",
				"QR Code Entry",
				"Your Own Subdomain",
				"Real-Time Analytics",
				"Email & SMS Outreach",
				"payment processing",
				"Music Recommendations",
				"financial reporting",
			],

			stats: [
				{ num: "8", lbl: "Real events hosted " },
				{ num: "200+", lbl: "Tickets booked in beta" },
				{ num: "100%", lbl: "QR entry success rate" },
				{ num: "5", lbl: "Organizers already using Trekko" },
			],

			problems: [
				{
					icon: "M37,39H11l-6,6V11c0-3.3,2.7-6,6-6h26c3.3,0,6,2.7,6,6v22C43,36.3,40.3,39,37,39z",
					title: "Managing 5–6 WhatsApp groups for updates",
					body: "No central place to reach your audience. Messages get lost. People miss events.",
				},
				{
					icon: "M14 4C8.48 4 4 8.48 4 14v20c0 5.52 4.48 10 10 10h20c5.52 0 10-4.48 10-10V14c0-5.52-4.48-10-10-10H14zm10 12a8 8 0 100 16 8 8 0 000-16zm11-3a2 2 0 110 4 2 2 0 010-4z",
					title: "Selling tickets through Instagram DMs",
					body: "Manual, untracked, and unprofessional. No receipts. No data. No control.",
				},
				{
					icon: "M12 4h18c2.21 0 4 1.79 4 4v32c0 2.21-1.79 4-4 4H12V4zm4 6v28h12V10H16zm10 12a2 2 0 110 4 2 2 0 010-4z",
					title: "VIP guests unrecognized at the door",
					body: "Long queues, confusion, fraud. A ruined experience for everyone — including you.",
				},
				{
					icon: "M8 40h32v4H8v-4zm4-16h4v12h-4V24zm8-8h4v20h-4V16zm8-8h4v28h-4V8z",
					title: "Zero data on who attended or why they came",
					body: "No insights means no improvement. You can't grow what you can't measure.",
				},
			],

			steps: [
				{
					num: "1",
					title: "Simple, Streamlined Onboarding",
					body: "Quick application — profile creation in minutes",
				},
				{
					num: "2",
					title: "Quick Vetting, Real Standards",
					body: "Every profile reviewed before going live",
				},
				{
					num: "3",
					title: "Made for Your Kind of Talent",
					body: "Any event type, any scale — we have you covered",
				},
			],

			dashFeatures: [
				{
					title: "Sales Velocity Trend",
					body: "See ticket sales grow in real time by year, month, or event.",
					icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z",
				},
				{
					title: "Event Status Tracking",
					body: "Active, completed, cancelled — always know where each event stands.",
					icon: "M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z",
				},
				{
					title: "Top Performing Events",
					body: "Instantly see which events sold the most tickets and generated the most revenue.",
					icon: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
				},
				{
					title: "Stripe Revenue & Payouts",
					body: "Connected to Stripe. Withdraw earnings and view full payout history.",
					icon: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z",
				},
			],

			subdomainChecks: [
				"All your events in one shareable link",
				"Your bio, brand, and past events displayed professionally",
				// "Attendees can follow you and get notified of new events",
				"Works perfectly as a link in bio or printed QR code",
			],

			mockEvents: [
				{
					name: "Sound without Borders",
					date: "June 1, 2025 · 6:00 PM",
					status: "ended",
					label: "Ended",
				},
				{
					name: "Poetry Night",
					date: "May 1, 2025 · 7:30 PM",
					status: "ended",
					label: "Ended",
				},
				{
					name: "Cultural Aperitivo",
					date: "February 23, 2026 · 6:00 PM",
					status: "ended",
					label: "Ended",
				},
			],

			features: [
				{
					title: "Discover Events Near You",
					body: "Get personalized recommendations and explore events happening around you.",
					icon: "M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z",
				},
				{
					title: "Boost Your Event's Visibility",
					body: "Reach more attendees locally and globally with smart event discovery.",
					icon: "M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535",
				},
				{
					title: "Engage Your Audience",
					body: "Connect with attendees through email marketing and bulk SMS.",
					icon: "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Z",
				},
				{
					title: "Seamless Ticket Scanning",
					body: "Go paperless with fast, secure QR code entry at your event.",
					icon: "M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 3.75 9.375v-4.5ZM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5ZM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 13.5 9.375v-4.5Z",
				},
				{
					title: "Account Management",
					body: "Easily manage your profile, organizations, roles, and event settings in one secure dashboard.",
					icon: "M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z",
				},
				{
					title: "Payment Integration",
					body: "Accept payments securely through our integrated payment gateway.",
					icon: "M14.25 7.756a4.5 4.5 0 1 0 0 8.488M7.5 10.5h5.25m-5.25 3h5.25M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
				},
				// {
				// 	title: "Dynamic Pricing",
				// 	body: "Adjust ticket prices based on demand to maximize revenue.",
				// 	icon: "M14.25 7.756a4.5 4.5 0 1 0 0 8.488M7.5 10.5h5.25m-5.25 3h5.25M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
				// },
				{
					title: "Track Revenue & Expenses",
					body: "Monitor ticket income, payouts, and costs with a built-in ledger for full financial visibility.",
					icon: "M4.5 3.75h15A1.5 1.5 0 0 1 21 5.25v13.5A1.5 1.5 0 0 1 19.5 20.25h-15A1.5 1.5 0 0 1 3 18.75V5.25A1.5 1.5 0 0 1 4.5 3.75Zm3 3h9m-9 3h9m-9 3h6",
				},
				{
					title: "Smart Analytics Dashboard",
					body: "Track ticket sales, audience insights, and booking trends in real time.",
					icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z",
				},
				{
					title: "Understand Your Audience",
					body: "Gather feedback and preferences to improve future events.",
					icon: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235",
				},
			],

			faqs: [
				{
					q: "What is Trekko?",
					a: "Trekko is an organizer infrastructure platform designed to help event organizers run events more efficiently. From event creation and registrations to attendee engagement, ticketing, and analytics, Trekko brings the essential tools organizers need into a single platform.",
				},
				{
					q: "Is it really free to start?",
					a: "Yes — creating an organizer profile, publishing events, and managing attendees is completely free. Trekko only charges a small service fee on paid ticket transactions. There are no setup costs, monthly commitments, or hidden charges, so you can start organizing events right away and only pay when you generate ticket sales.",},
				{
					q: "What is the subdomain feature?",
					a: "Your personalized Trekko page gives you a unique web presence where all your events, organization details, and updates are showcased in one place. Easily share it with your audience through social media, messaging apps, or QR codes.",
				},
				{
					q: "How does QR entry scanning work?",
					a: "When a ticket is purchased, the attendee receives a unique QR code. At the door, your team scans it with any smartphone — no special hardware needed. In our beta, this has a 100% success rate with zero fraud incidents.",
				},
				{
					q: "What is the music recommendation feature?",
					a: "The Music Recommendation feature allows attendees to suggest songs before the event. Organizers can use these recommendations to build playlists that reflect their audience's preferences and enhance the overall event experience.",
				},
				{
					q: "How do I get paid?",
					a: "Your earnings are available in the Revenue section of your dashboard and can be withdrawn to your connected bank account at any time. Payments and payouts are handled securely through Trekko.",
				},
			],
		};
	},

	mounted() {
		window.addEventListener("scroll", this.onScroll);
	},

	beforeUnmount() {
		window.removeEventListener("scroll", this.onScroll);
	},

	methods: {
		onScroll() {
			this.scrolled = window.scrollY > 20;
		},
		toggleFaq(i) {
			this.openFaq = this.openFaq === i ? null : i;
		},
	},
};
</script>

<style>
/* ── Fonts ── */
@import url("https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter:wght@400;500;600&display=swap");

.font-syne {
	font-family: "Montserrat", sans-serif;
}

/* ── Keyframe animations (not doable with Tailwind utilities alone) ── */
@keyframes blink {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.35;
	}
}
@keyframes pulse-g {
	0%,
	100% {
		box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.25);
	}
	50% {
		box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.08);
	}
}
@keyframes slide {
	from {
		transform: translateX(0);
	}
	to {
		transform: translateX(-50%);
	}
}

.animate-blink {
	animation: blink 2s infinite;
}
.animate-pulse-green {
	animation: pulse-g 1.6s infinite;
}
.animate-slide {
	animation: slide 22s linear infinite;
}
</style>
